import React, { ReactElement, useEffect, useState } from 'react';
import LegalEntitiesChart from 'pages/DashboardPage/PieCharts/LegalEntitiesChart';
import Grid from '@mui/material/Grid';
import FirearmsChart from 'pages/DashboardPage/PieCharts/FirearmsChart';
import AncillariesChart from 'pages/DashboardPage/PieCharts/AncillariesChart';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import TransactionsChart from 'pages/DashboardPage/BarCharts/TransactionsChart';
import FirearmsStatesChart from 'pages/DashboardPage/BarCharts/FirearmsStatesChart';
import GlobalSearch from 'pages/DashboardPage/GlobalSearch';
import { StyledDashboardButton } from 'pages/DashboardPage/GlobalSearch/styles';
import { EDashboardSortType } from 'models/dashboard';
import { DashboardSortButton } from 'pages/DashboardPage/DashboardSortButton';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { StyledDashboardSortButtonsWrapper } from 'pages/DashboardPage/styles';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { Loader } from 'components/atoms/Loader';
import { Box } from '@mui/system';
import { useAuth } from 'models/authContext';
import { areArraysEqual } from 'utils/arrays';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { DashboardSortSelect } from './DashboardSortSelect';

const DashboardPage = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { hasPermissions } = usePermission();
  const [selectedSortType, setSelectedSortType] = useState<EDashboardSortType>(
    EDashboardSortType.All,
  );
  const [isLoader, setLoader] = useState(true);
  const [isFirearmsStatesLoader, setFirearmsStatesLoader] = useState(true);
  const [isTransactionsLoader, setTransactionsLoader] = useState(true);
  const [isFirearmsLegalityLoader, setFirearmsLegalityLoader] = useState(true);
  const [isAncillariesTypesLoader, setAncillariesTypesLoader] = useState(true);
  const [isLegalEntitiesLoader, setLegalEntitiesLoader] = useState(true);
  const { user, getUser } = useAuth();
  const [isOnlyState, setIsOnlyState] = useState<null | boolean>(null);

  useEffect(() => {
    if (
      !isFirearmsStatesLoader &&
      !isTransactionsLoader &&
      !isFirearmsLegalityLoader &&
      !isLegalEntitiesLoader &&
      !isAncillariesTypesLoader
    ) {
      setLoader(false);
    } else {
      setLoader(true);
    }
  }, [
    isFirearmsStatesLoader,
    isTransactionsLoader,
    isFirearmsLegalityLoader,
    isLegalEntitiesLoader,
    isAncillariesTypesLoader,
  ]);

  useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setIsOnlyState(
        areArraysEqual(
          [...(user?.organisationCategoryIds ?? []), ...(user?.prefixGroupIds ?? [])],
          [EOrganisationCategory.state],
        ),
      );
    }
  }, [user]);

  return (
    <>
      <StyledPageTitle variant="h4">{t('module.dashboard.name')}</StyledPageTitle>
      <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
        {isOnlyState === null && (
          <Grid size={{ xs: 12 }}>
            <Loader isLoading />
          </Grid>
        )}
        {hasPermissions(EPermission.MANAGE_LE) && isOnlyState !== null && (
          <Grid container size={{ xs: 12 }} spacing={2} sx={{ mb: 1, mt: 1 }}>
            {!isOnlyState && (
              <Grid size={{ xs: 12, lg: 6 }}>
                <StyledDashboardButton
                  variant={EButtonVariants.contained}
                  icon={EIconTypes.plus}
                  label={t('dashboard.button.add.legal.entity.individual')}
                  fullWidth
                  onClick={() => navigate(`${ERouteLinks.AddLegalEntity}?type=individual`)}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, lg: isOnlyState ? 12 : 6 }}>
              <StyledDashboardButton
                variant={EButtonVariants.contained}
                icon={EIconTypes.plus}
                label={t('dashboard.button.add.legal.entity.organisation')}
                fullWidth
                onClick={() => navigate(`${ERouteLinks.AddLegalEntity}?type=organisation`)}
              />
            </Grid>
          </Grid>
        )}

        <Grid size={{ xs: 12 }} sx={{ mb: 1, mt: 1 }}>
          <GlobalSearch query={''} category={[]} />
        </Grid>

        {hasPermissions(EPermission.VIEW_DASHBOARD_CHARTS) && (
          <>
            {!isDesktop && (
              <Grid size={{ xs: 12 }} sx={{ my: 1 }}>
                <DashboardSortSelect
                  selectedSortType={selectedSortType}
                  setSelectedSortType={setSelectedSortType}
                />
              </Grid>
            )}

            {isDesktop && (
              <Grid container size={{ xs: 12 }} sx={{ mb: 2 }}>
                <StyledDashboardSortButtonsWrapper>
                  {Object.values(EDashboardSortType).map((sortType) => (
                    <DashboardSortButton
                      key={sortType}
                      selected={selectedSortType === sortType}
                      setSelectedSortType={setSelectedSortType}
                      sortType={sortType}
                    />
                  ))}
                </StyledDashboardSortButtonsWrapper>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              {isLoader && <Loader isLoading={isLoader} />}
              <Box sx={{ display: !isLoader ? 'block' : 'none' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <LegalEntitiesChart
                      sortType={selectedSortType}
                      setLoader={setLegalEntitiesLoader}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FirearmsChart
                      sortType={selectedSortType}
                      setLoader={setFirearmsLegalityLoader}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <AncillariesChart
                      sortType={selectedSortType}
                      setLoader={setAncillariesTypesLoader}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginTop: '10px' }}>
                  <Grid size={{ xs: 12, sm: 6, lg: 6 }} sx={{ display: 'grid' }}>
                    <FirearmsStatesChart
                      sortType={selectedSortType}
                      setLoader={setFirearmsStatesLoader}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 6 }} sx={{ display: 'grid' }}>
                    <TransactionsChart
                      sortType={selectedSortType}
                      setLoader={setTransactionsLoader}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};
export { DashboardPage };
